import { Fragment, useEffect, useRef } from 'react'
import cn from 'classnames'
import { ConfirmedSegment, gapBefore, isPause } from '../../../shared/labs/transcript'
import { KeepTalkingModal } from '../../../shared/components/KeepTalkingModal'

// The persistent play surface: the four numbers and the running transcript. Used for
// the whole post-commit journey too (reflection → result), so the player keeps seeing
// what they said while the confirmation messages appear underneath (`children`).
// `middle` is an optional slot between numbers and transcript (the energy bar, during play).
export const PuzzleStage = ({
  numbers,
  dimmed = false,
  confirmedSegments,
  interimText,
  progressLabel,
  showNudge = false,
  middle,
  children,
}: {
  numbers: number[]
  dimmed?: boolean
  confirmedSegments: ConfirmedSegment[]
  interimText: string
  progressLabel?: string
  showNudge?: boolean
  middle?: React.ReactNode
  children?: React.ReactNode
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [confirmedSegments, interimText, children])

  return (
    <div className="flex flex-col items-center px-6 pt-10 pb-4">
      <div className="w-full max-w-lg">
        {progressLabel && (
          <p className="text-center text-xs font-medium uppercase tracking-wide text-gray-400">{progressLabel}</p>
        )}
        <p className="mt-1 text-center text-gray-700">
          Use <span className="font-semibold text-gray-900">all four</span> numbers — each one{' '}
          <span className="font-semibold text-gray-900">once</span> — to make{' '}
          <span className="font-semibold text-gray-900">24</span>.
        </p>
        <p className="mt-1 text-center text-xs text-gray-400">
          Combine them with + − × ÷ and brackets. You can’t join digits, so 2 and 3 can’t make 23.
        </p>

        <div className="relative mt-6">
          <div className="grid grid-cols-4 gap-3">
            {numbers.map((n, i) => (
              <div key={i} className="relative aspect-square rounded-xl bg-[#820458] text-white shadow">
                {/* The number fades out and a "?" fades in when energy is depleted
                    (no talking), so the puzzle is genuinely hidden — not just blurred. */}
                <span
                  className={cn(
                    'absolute inset-0 flex items-center justify-center text-3xl font-bold transition-opacity duration-700',
                    dimmed ? 'opacity-0' : 'opacity-100'
                  )}
                >
                  {n}
                </span>
                <span
                  className={cn(
                    'absolute inset-0 flex items-center justify-center text-3xl font-bold transition-opacity duration-700',
                    dimmed ? 'opacity-70' : 'opacity-0'
                  )}
                >
                  ?
                </span>
              </div>
            ))}
          </div>

          {/* "Keep talking" nudge sits BELOW the tiles AND clear of the energy bar (which
              occupies ~+24–56px below the tiles). top-full drops it to the tile bottom;
              translate-y-2/3 (~2/3 of the ~100px card ≈ 67px) pushes its top to ~+67px, past
              the energy bar, over the transcript area (empty while silent). Never covers the
              numbers. pointer-events-none keeps the controls underneath usable. */}
          {showNudge && (
            <div className="pointer-events-none absolute inset-x-0 top-full z-40 translate-y-2/3 flex justify-center px-2">
              <KeepTalkingModal />
            </div>
          )}
        </div>

        {middle && <div className="mt-6">{middle}</div>}

        {/* Running transcript — live feedback that we're hearing the player. Utterances
            flow inline; precise pause timing lives in the saved transcript timestamps,
            not on screen. Stays visible after commit so the messages below have context. */}
        <div className="mt-4 text-xs text-gray-400">What we’re hearing</div>
        <div className="relative mt-1">
          <div
            ref={scrollRef}
            className="h-40 overflow-y-auto rounded-lg bg-gray-50 px-3 py-2 text-sm leading-relaxed"
          >
            {confirmedSegments.length === 0 && !interimText && (
              <span className="text-gray-400 italic">Start talking through your idea…</span>
            )}
            {confirmedSegments.map((s, i) => {
              const gap = gapBefore(confirmedSegments, i)
              return (
                <Fragment key={i}>
                  {i > 0 &&
                    (isPause(gap) ? (
                      <span className="mx-1 text-xs text-gray-400 select-none whitespace-nowrap align-middle">
                        {'<'}
                        {(gap / 1000).toFixed(1)}s{'>'}
                      </span>
                    ) : (
                      ' '
                    ))}
                  <span className="text-gray-700">{s.text}</span>
                </Fragment>
              )
            })}
            {interimText && <span className="text-gray-400 italic"> {interimText}</span>}
          </div>
        </div>

        {children && <div className="mt-5 pb-10">{children}</div>}
      </div>
    </div>
  )
}
